<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
	<button
		ref="mainElem"
		class="c-Select"
		:class="selectClassObject"
		:title="label"
		:disabled="disabled"
		@keydown.down.prevent
		@keydown.up.prevent
		@click.self="focusElem"
		@focus.capture="toggleFocus(true)"
		@blur.capture="toggleFocus(false)"
	>
		<transition name="fade">
			<span
				v-if="currValue.label"
				class="c-Select__value"
				@click.self="focusElem"
			>
				{{ currValue.label }}
			</span>
		</transition>
		<span
			class="c-Select__label"
			@click.self="focusElem"
		>
			{{ label }}
		</span>
		<i
			v-show="currValue.value"
			v-if="withClear"
			class="c-Select__clear"
			@click="clearSelection"
			@mousedown.prevent
		/>
		<i class="c-Select__decorator" />
		<ul
			v-if="menuVisible"
			class="c-Select__menu"
		>
			<li
				v-for="(opt, index) in selectItems"
				:key="index"
				class="c-Select__menuItem"
			>
				<button
					type="button"
					:key-index="index"
					@click="selectOption(opt)"
					@mousedown.prevent
				>
					{{ opt.label }}
				</button>
			</li>
		</ul>
	</button>
</template>

<script>
export default {
	name: 'Select',
	props: {
		/**
     * The Select label
     */
		label: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The Select options for the dropdown
     */
		selectItems: {
			type: Array,
			required: true,
			default: () => [],
			validator: (data) => {
				const conditions = []
				data.forEach((item) => {
					conditions.push(Object.keys(item).indexOf('label'))
					conditions.push(Object.keys(item).indexOf('value'))
				})

				return data.length === 0 || conditions.every(cond => cond > -1)
			}
		},
		/**
     * The Select data variable
     */
		data: {
			type: [Number, String],
			required: false,
			default: () => undefined
		},
		/**
     * If the Select should display a clear input button
     * @values true, false
     */
		withClear: {
			type: Boolean,
			required: false,
			default: () => true
		},
		/**
     * If the Select is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the Select is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			menuVisible: false,
			currValue: {
				label: null,
				value: null
			}
		}
	},
	computed: {
		selectClassObject () {
			return {
				open: this.menuVisible,
				withValue: this.currValue.label && this.currValue.label.length > 0,
				isActive: this.isActive
			}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				const option = this.selectItems.find(item => item.value === newVal)

				if (option) {
					this.currValue.label = option.label
					this.currValue.value = newVal
				} else if (newVal === null || newVal === undefined) {
					this.currValue.label = null
					this.currValue.value = null
				}
			},
			immediate: true
		}
	},
	mounted () {
		this.$refs.mainElem.addEventListener('keydown', this.keyPress, true)
	},
	beforeDestroy () {
		this.$refs.mainElem.removeEventListener('keydown', this.keyPress, true)
	},
	methods: {
		toggleFocus (arg) {
			this.menuVisible = arg
		},
		focusElem () {
			this.$refs.mainElem.focus()
		},
		selectOption (option) {
			if (option.value !== this.currValue.value) {
				this.currValue.value = option.value
				this.currValue.label = option.label
				this.$emit('change', this.currValue.value)
			}

			document.activeElement.blur()
		},
		clearSelection () {
			if (!this.disabled) {
				this.currValue = {
					label: null,
					value: null
				}
				/**
         * Triggers when Select changes it's value
         * @event change
         * @property {string} value new value set
         */
				this.$emit('change', this.currValue.value)
			}
		},
		keyPress (ev) {
			const currIndex = document.activeElement.getAttribute('key-index')
			const elemCount = document.querySelector('.c-Select__menu') ? document.querySelector('.c-Select__menu').childElementCount : 0

			if (ev.key === 'ArrowDown') {
				let index = 0

				if (currIndex !== null) {
					index = parseInt(currIndex, 10)
					index += 1
				}

				if (index <= elemCount - 1) {
					document.querySelector(`.c-Select__menu li button[key-index="${index}"]`).focus()
				}
			} else if (ev.key === 'ArrowUp') {
				let index = currIndex !== null ? parseInt(currIndex, 10) : 0
				index -= 1
				if (index >= 0) {
					document.querySelector(`.c-Select__menu li button[key-index="${index}"]`).focus()
				}
			} else if (ev.key === 'Escape') {
				document.activeElement.blur()
			} else if (ev.key === 'Backspace' && this.withClear) {
				this.clearSelection()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';
$padding-right: 60px;

.c-Select {
  $font-size-label: 16px;
  $font-size-label-top: 12px;
  $font-size-value: 16px;
  $font-size-dropdown: 14px;

  position: relative;
  outline: none;
  height: 50px;
  width: 100%;
  display: block;
  box-sizing: border-box;
  padding: calc(#{$base-padding} / 2) $padding-right
    calc(#{$base-padding} / 2) calc(#{$base-padding} / 2);
  color: $un-n700;
  border-radius: $border-radius;
  border: 1px solid $un-gray-light;
  background-color: $un-white;
  cursor: pointer;
  text-align: left;
  position: relative;

  &:disabled {
    border-color: $un-n500;
    background-color: $un-n100;
		cursor: not-allowed;

    & .c-Select__label,
    & .c-Select__value {
      color: $un-n500;
    }

    & .c-Select__clear {
			cursor: not-allowed;
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%238D98AA" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }

    & .c-Select__decorator {
      background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%238D98AA" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
    }
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &.open {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;
    outline: none;

    & .c-Select__label {
      color: $un-blue;
    }

    & .c-Select__decorator {
      background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%233843d0" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
      transform: rotate(180deg);
    }
  }

  &.isActive {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;
    outline: none;

    & .c-Select__label {
      color: $un-blue;
    }
  }

  &.withValue {
    .c-Select__label {
      font-size: $font-size-label-top;
      top: calc(#{$base-padding} / 2.4);
    }
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  &__label {
    display: block;
    width: calc(100% - #{$padding-right} - calc(#{$base-padding} / 2));
    font-size: $font-size-label;
    color: $un-n700;
    position: absolute;
    top: 14px;
    left: calc(#{$base-padding} / 2);
    z-index: 2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: all $default-time $default-easing;
  }

  &__value {
    width: 100%;
    font-size: $font-size-value;
    color: $un-n700;
    letter-spacing: 0.5px;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: calc(#{$base-margin} - 2px);
    opacity: 1;
    transition: opacity $default-time $default-easing;
  }

  &__clear {
    width: 20px;
    height: 20px;
    position: absolute;
    top: 14px;
    right: 36px;
    cursor: pointer;
    background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%231C357B" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    z-index: 2;

    &:hover {
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%233843d0" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }
  }

  &__decorator {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    content: '';
    display: block;
    width: 42px;
    pointer-events: none;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%23343F50" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
  }

  &__menu {
    position: absolute;
    bottom: -8px;
    left: 0;
    transform: translate(0, 100%);
    z-index: 5;
    list-style: none;
    margin: 0;
    padding: 0;
    min-width: 100%;
    max-height: calc(48px * 4);
    overflow-x: hidden;
    overflow-y: auto;
    background-color: $un-white;
    border-radius: $border-radius;
    box-shadow: 0px 3px 6px 0px rgba(174, 185, 203, 0.6), 0px 3px 6px 0px rgba(174, 185, 203, 0.6);
  }

  &__menuItem {
    margin: 0;
    padding: 0;

    button {
      width: 100%;
      height: 100%;
      background-color: transparent;
      color: $un-gray-bd;
      border: none;
      min-height: 48px;
      padding: $base-padding;
      cursor: pointer;
      font-size: $font-size-dropdown;
      text-align: left;
      outline: none;

      &::-moz-focus-inner {
        border: 0;
      }

      &:hover, &:focus, &.selected:hover, &.selected:focus {
        background-color: $un-blue-light;
        color: $un-gray-bd;
      }

      &.selected {
        background-color: $un-blue;
        color: #FFFFFF;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@unbabel/ui/src/components/Select/Select.vue